import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, Loader2, RefreshCw, Shield } from 'lucide-react';
import AdminPagesAuditTableSection from './AdminPagesAuditTableSection';
import AdminPagesAuditSummary from './AdminPagesAuditSummary';
import AdminPagesAuditDialog from './AdminPagesAuditDialog';
import { AUDIT_STATUS_OPTIONS, CATEGORY_LABELS, CATEGORY_ORDER } from './AdminPagesAudit.constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPagesAudit() {
  const [sitePages, setSitePages] = useState([]);
  const [sitePagesLoading, setSitePagesLoading] = useState(true);
  const [sitePagesSaving, setSitePagesSaving] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [auditForm, setAuditForm] = useState({ audit_status: 'a_verifier', audit_notes: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const accessBadgeClass = (access) => {
    if (access === 'Manager+') return 'bg-amber-500 text-white';
    if (access === 'Directeur') return 'bg-red-600 text-white';
    if (access === 'Public') return 'bg-green-600 text-white';
    return 'bg-slate-500 text-white';
  };

  const getRouteSlug = (route) => route.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();

  const getStatusMeta = (status) => {
    return AUDIT_STATUS_OPTIONS.find((option) => option.value === status) || AUDIT_STATUS_OPTIONS[0];
  };

  const formatAuditUpdate = (page) => {
    if (!page.audit_updated_at) return '—';
    const formatted = new Date(page.audit_updated_at).toLocaleString('fr-CH', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
    return page.audit_updated_by_name ? `${formatted} • ${page.audit_updated_by_name}` : formatted;
  };

  const fetchSitePages = useCallback(async () => {
    setSitePagesLoading(true);
    try {
      const res = await axios.get(`${API}/site-pages`);
      setSitePages(res.data.pages || []);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors du chargement des pages'));
    } finally {
      setSitePagesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSitePages();
  }, [fetchSitePages]);

  const openAuditDialog = (page) => {
    setSelectedPage(page);
    setAuditForm({
      audit_status: page.audit_status || 'a_verifier',
      audit_notes: page.audit_notes || ''
    });
    setAuditDialogOpen(true);
  };

  const handleSaveAudit = async () => {
    if (!selectedPage) return;
    setSitePagesSaving(true);
    try {
      await axios.put(`${API}/site-pages/audit`, {
        route: selectedPage.route,
        audit_status: auditForm.audit_status,
        audit_notes: auditForm.audit_notes
      });
      toast.success('Audit mis à jour');
      setAuditDialogOpen(false);
      setSelectedPage(null);
      fetchSitePages();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de la mise à jour'));
    } finally {
      setSitePagesSaving(false);
    }
  };

  const filteredPages = sitePages.filter((page) => {
    const matchesStatus = statusFilter === 'all' || page.audit_status === statusFilter;
    const query = searchQuery.trim().toLowerCase();
    const matchesQuery = !query || page.route.toLowerCase().includes(query) || page.name.toLowerCase().includes(query);
    return matchesStatus && matchesQuery;
  });

  const groupedPages = CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = filteredPages.filter((page) => page.category === category);
    return acc;
  }, {});

  const categoryCounts = CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = sitePages.filter((page) => page.category === category).length;
    return acc;
  }, {});

  const statusCounts = sitePages.reduce((acc, page) => {
    const status = page.audit_status || 'a_verifier';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const cleanupCount = sitePages.filter((page) => ['dupliquee', 'obsolete', 'a_supprimer'].includes(page.audit_status)).length;

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6" data-testid="admin-pages-tab">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Liste des Pages du Site
          </h2>
          <p className="text-sm text-muted-foreground" data-testid="pages-audit-subtitle">
            Audit et nettoyage des routes (pages inutiles, duplications, obsolètes)
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchSitePages}
          data-testid="pages-refresh-button"
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Rafraîchir
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Filtre par statut</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="pages-status-filter">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {AUDIT_STATUS_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2 md:col-span-2">
          <Label>Recherche</Label>
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une route ou une page"
            data-testid="pages-search-input"
          />
        </div>
      </div>

      <div className="p-4 bg-muted rounded-lg flex items-start gap-3" data-testid="pages-cleanup-callout">
        <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
        <div>
          <p className="font-medium text-sm">Pages à nettoyer : {cleanupCount}</p>
          <p className="text-xs text-muted-foreground">Marquez les pages en double, obsolètes ou à supprimer pour préparer le nettoyage.</p>
        </div>
      </div>

      {sitePagesLoading ? (
        <div className="flex items-center justify-center py-8" data-testid="pages-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-4">
          {CATEGORY_ORDER.map((category) => (
            <AdminPagesAuditTableSection
              key={category}
              category={category}
              label={CATEGORY_LABELS[category]}
              pages={groupedPages[category]}
              accessBadgeClass={accessBadgeClass}
              getStatusMeta={getStatusMeta}
              getRouteSlug={getRouteSlug}
              formatAuditUpdate={formatAuditUpdate}
              onEdit={openAuditDialog}
            />
          ))}
        </div>
      )}

      <AdminPagesAuditSummary
        categoryOrder={CATEGORY_ORDER}
        categoryLabels={CATEGORY_LABELS}
        categoryCounts={categoryCounts}
        statusOptions={AUDIT_STATUS_OPTIONS}
        statusCounts={statusCounts}
      />

      <AdminPagesAuditDialog
        open={auditDialogOpen}
        onOpenChange={(open) => {
          setAuditDialogOpen(open);
          if (!open) {
            setSelectedPage(null);
          }
        }}
        selectedPage={selectedPage}
        auditForm={auditForm}
        setAuditForm={setAuditForm}
        onSave={handleSaveAudit}
        saving={sitePagesSaving}
        statusOptions={AUDIT_STATUS_OPTIONS}
      />
    </div>
  );
}